import clsx from "clsx";
import { FC, useMemo } from "react";
import { twMerge } from "tailwind-merge";

import { SectionIcon } from "components/materials/section/SectionIcon";
import { FullSectionType } from "components/server-types";
import { t } from "i18n/i18n";
import {
  Listbox,
  type ListboxEntry,
  type ListboxGroup,
  type ListboxOption,
} from "mds/components/Listbox";
import { Tooltip } from "mds/components/Tooltip";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { ChevronRightIcon, CircleCheckIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  courseSelectors,
  sectionSelectors,
  selectCurrentSectionId,
  selectFullSectionsSorted,
  termSelectors,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { trackEvent } from "utils/amplitude";
import { getCourseCodeAndSectionTitle } from "utils/course";
import { isLinkedSection } from "utils/publishing";
import { useNavigate } from "utils/routing";
import { sectionHomeUrl, useViewLevel } from "utils/urls";
import { isCCMEnabled } from "utils/waffle-flags";

type SectionListboxOption = ListboxOption & { sectionTitle: string };

type NarrowedFullSectionType = Pick<
  FullSectionType,
  | "id"
  | "title"
  | "kind"
  | "template_id"
  | "term_id"
  | "course_code_section_title"
  | "lms_section_id"
>;

type GroupedSection = {
  section: NarrowedFullSectionType;
  linkedSections: NarrowedFullSectionType[];
};

const buildSectionOption = (
  section: NarrowedFullSectionType,
  {
    className,
    preferStaffIcons,
    showLinkedBadge,
  }: {
    className?: string;
    preferStaffIcons: boolean;
    showLinkedBadge: boolean;
  },
): SectionListboxOption => ({
  id: section.id,
  title: section.course_code_section_title,
  sectionTitle: section.title,
  className,
  iconStart: <SectionIcon preferStaffIcons={preferStaffIcons} sectionId={section.id} />,
  iconEnd:
    showLinkedBadge && section.lms_section_id ? (
      <div className="flex shrink-0 items-center gap-1" title={section.course_code_section_title}>
        <CircleCheckIcon className="text-green" />
        <span>{t("main_header.section_linked")}</span>
      </div>
    ) : undefined,
});

/**
 * A dropdown list of sections used in the main header.
 */
export const SectionSelect: FC<{
  className?: string;
  disabled?: boolean;
  disabledTooltipText?: string;
  isMobile?: boolean;
  openInNewTab?: boolean;
}> = ({ className, disabled, disabledTooltipText, isMobile, openInNewTab }) => {
  const navigate = useNavigate();
  const currentSectionId = useAppSelector(selectCurrentSectionId) || "";
  const sections = useListSelector(selectFullSectionsSorted, [
    "id",
    "title",
    "kind",
    "template_id",
    "term_id",
    "course_code_section_title",
    "lms_section_id",
  ]);
  const terms = useListSelector(termSelectors.selectAll, ["id", "title"]);
  const { canAuthorOrg, canAuthorSection } = useCapabilities();
  const viewLevel = useViewLevel();
  const isHomePage = viewLevel.home;
  const isSectionMainPage = viewLevel.section;
  const isSectionPage = viewLevel.section || viewLevel.page;
  // Resolve separately so an archived current section still gets a label.
  const currentSection = useObjectSelector(
    (state) => sectionSelectors.selectById(state, currentSectionId),
    ["title", "course_id"],
  );
  const currentCourse = useObjectSelector(
    (state) => courseSelectors.selectById(state, currentSection?.course_id),
    ["code"],
  );
  const currentSectionLabel = isHomePage
    ? t("main_header.select_section")
    : currentSection && currentCourse
      ? getCourseCodeAndSectionTitle(currentCourse, currentSection)
      : undefined;
  const ccmEnabled = isCCMEnabled();
  const preferStaffIcons = canAuthorOrg;
  const showLinkedBadge = canAuthorOrg || canAuthorSection;

  const groupedSections: GroupedSection[] = useMemo(() => {
    if (!ccmEnabled) {
      return sections.map((section) => ({
        section,
        linkedSections: [] as FullSectionType[],
      }));
    }

    const sectionIds = new Set(sections.map((section) => section.id));
    const linkedSectionsByTemplateId = new Map<string, NarrowedFullSectionType[]>();
    for (const section of sections) {
      if (isLinkedSection(section) && section.template_id && sectionIds.has(section.template_id)) {
        const list = linkedSectionsByTemplateId.get(section.template_id) ?? [];
        list.push(section);
        linkedSectionsByTemplateId.set(section.template_id, list);
      }
    }
    const grouped: GroupedSection[] = [];
    for (const section of sections) {
      if (
        !(isLinkedSection(section) && section.template_id && sectionIds.has(section.template_id))
      ) {
        grouped.push({
          section,
          linkedSections: linkedSectionsByTemplateId.get(section.id) ?? [],
        });
      }
    }
    return grouped;
  }, [sections, ccmEnabled]);

  const listboxItems = useMemo((): Array<ListboxEntry<SectionListboxOption>> => {
    const sectionsByTermId = new Map<string, GroupedSection[]>();
    for (const entry of groupedSections) {
      const termId = entry.section.term_id;
      const list = sectionsByTermId.get(termId) ?? [];
      list.push(entry);
      sectionsByTermId.set(termId, list);
    }

    const sortedTerms = terms.filter((term) => sectionsByTermId.has(term.id));

    return sortedTerms.map(
      (term): ListboxGroup<SectionListboxOption> => ({
        type: "group",
        title: term.title,
        // Order preserved from selectFullSectionsSorted (full_title).
        items: (sectionsByTermId.get(term.id) ?? []).flatMap(({ section, linkedSections }) => [
          buildSectionOption(section, {
            preferStaffIcons,
            showLinkedBadge,
          }),

          ...linkedSections.map((linked) =>
            buildSectionOption(linked, {
              className: "pl-7",
              preferStaffIcons,
              showLinkedBadge,
            }),
          ),
        ]),
      }),
    );
  }, [groupedSections, preferStaffIcons, showLinkedBadge, terms]);

  const hasSectionsToSelect = groupedSections.length > 0;

  const buttonClasses = twMerge(
    "group min-w-0 max-w-full",
    isMobile && "!h-[32px] !max-h-[380px]",
    className,
  );

  const sectionTitleTooltipText = disabled ? disabledTooltipText : "";

  const chevronClasses = clsx(
    "transition-transform duration-200 ease-in-out",
    (!isSectionPage || isMobile) && "rotate-90",
    isSectionPage && !isMobile && "group-hover:rotate-90 group-data-[selected=true]:rotate-90",
  );

  const sectionIcon = currentSectionId && (
    <SectionIcon preferStaffIcons={preferStaffIcons} sectionId={currentSectionId} size="s" />
  );

  return (
    <div className={clsx("flex min-w-0 gap-2", isMobile && "flex-col")}>
      {hasSectionsToSelect && (
        <Tooltip content={sectionTitleTooltipText}>
          <Listbox
            disabled={disabled}
            items={listboxItems}
            itemSize="xs"
            openMenuEventName="Open My Courses menu"
            selectedValue={currentSectionId}
            triggerButton={
              <SquareButton
                className={buttonClasses}
                disabled={disabled}
                kind="ghost"
                size={isMobile ? "m" : "s"}
              >
                <span className="block min-w-0 truncate">
                  {isMobile ? currentSectionLabel : t("main_header.select_section")}
                </span>
                <ChevronRightIcon className={chevronClasses} />
              </SquareButton>
            }
            onSelectionChange={(item) => {
              trackEvent("Section Selected", {
                eventCategory: "Button press",
                sectionTitle: item.sectionTitle,
              });
              const url = sectionHomeUrl(item.id);
              if (openInNewTab) {
                window.open(url, "_blank", "noopener");
              } else {
                navigate(url);
              }
            }}
          />
        </Tooltip>
      )}
      {!isMobile && isSectionMainPage ? (
        <span className="flex h-[34px] min-w-0 flex-1 items-center gap-2 overflow-hidden border border-transparent px-[10px] text-sm font-normal text-black-tint-20 group-data-[dark]/themable:text-white">
          {sectionIcon}
          <span className="block min-w-0 truncate" title={currentSectionLabel}>
            {currentSectionLabel}
          </span>
        </span>
      ) : (
        !isMobile &&
        isSectionPage && (
          <div className="min-w-0 flex-1 overflow-hidden">
            <Tooltip content={sectionTitleTooltipText}>
              <SquareButton
                className={twMerge(buttonClasses, "min-w-0 max-w-full gap-2")}
                disabled={disabled}
                eventName="Go to Course Overview"
                kind="ghost"
                size="s"
                onClick={() => {
                  navigate(sectionHomeUrl(currentSectionId));
                }}
              >
                {sectionIcon}
                <span className="block min-w-0 truncate" title={currentSectionLabel}>
                  {currentSectionLabel}
                </span>
              </SquareButton>
            </Tooltip>
          </div>
        )
      )}
    </div>
  );
};
